import { Box, MenuItem, Stack, TextField } from "@mui/material";
import { Button, Typography } from "@platform/design-system";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import {
  CustomTextEditorFields,
  CustomTextModeToggle,
} from "./CustomTextEditorFields";
import { writeCollapsedSiblings, writeSibling } from "./siblingWrites";
import { useCustomTextEditor } from "./useCustomTextEditor";
import { usePopupEditorKeyboard } from "./usePopupEditorKeyboard";

import type { ICellEditorParams } from "ag-grid-community";
import type { JsonValue } from "../utils/popupCustomText";

export interface PreExistingConditionTypeOption {
  value: string;
  label: string;
}

export interface PreExistingConditionCellEditorParams {
  initialType: string | undefined;
  /** The `"01/03"` or `"01/03/03"` display string from the collapsed sibling row. */
  initialMonthsDetail: string | undefined;
  typeKey: string;
  detailKey: string;
  typeOptions: PreExistingConditionTypeOption[];
  /** Upper bound for the month sub-fields, when the backend enforces one
   * (e.g. STD's ent schema caps these at 60) — undefined when unbounded. */
  monthsMax?: number;
  initialCustomText?: string | null;
  onCustomTextChange?: (
    customText: string | null,
    originalValue?: JsonValue,
  ) => void;
}

type Props = ICellEditorParams & PreExistingConditionCellEditorParams;

/**
 * Parse a `"bb/tt"` or `"bb/tt/aa"` months-detail string into three number strings.
 * Returns empty strings for missing or invalid parts.
 */
function parseMonthsDetail(detail: string | undefined): {
  before: string;
  treatment: string;
  after: string;
} {
  if (!detail) return { before: "", treatment: "", after: "" };
  const parts = detail.split("/");
  return {
    before: parts[0] ? String(parseInt(parts[0], 10) || "") : "",
    treatment: parts[1] ? String(parseInt(parts[1], 10) || "") : "",
    after: parts[2] ? String(parseInt(parts[2], 10) || "") : "",
  };
}

/** Zero-pad a month string to 2 digits, matching the BTX transform format. */
function padMonth(value: string): string {
  const n = parseInt(value, 10);
  if (isNaN(n) || n < 0) return "00";
  return String(n).padStart(2, "0");
}

/** Validates a month input against the field's (optional) upper bound. */
function validateMonth(value: string, max: number | undefined): string {
  if (value === "") return "";
  const n = Number(value);
  if (!Number.isFinite(n)) return "Enter a number";
  // padMonth truncates via parseInt, so a fractional value (e.g. "1.5") would
  // silently save as a different number ("01") than what was entered.
  if (!Number.isInteger(n)) return "Enter a whole number";
  if (n < 0) return "Must be at least 0";
  if (max !== undefined && n > max) return `Must be at most ${max}`;
  return "";
}

/**
 * AG Grid popup cell editor for the Pre-Existing Condition field.
 *
 * Renders a type dropdown (e.g. "Months" / "Not Offered") and, when type is
 * "months", three number inputs for the individual month sub-fields
 * (diagnosis-free before, treatment-free, diagnosis-free after — the last
 * is optional). On save it writes the rebuilt `"bb/tt"` / `"bb/tt/aa"` string
 * back to the collapsed detail sibling and the type to the type sibling. A
 * zero or blank treatment-free value alongside an after value collapses to
 * `"bb/aa"`, the form the cell already renders.
 */
export const PreExistingConditionCellEditor = forwardRef<unknown, Props>(
  function PreExistingConditionCellEditor(props, ref) {
    const {
      initialType,
      initialMonthsDetail,
      typeKey,
      detailKey,
      typeOptions,
      monthsMax,
      initialCustomText,
      onCustomTextChange,
      api,
      colDef,
      column,
      node,
      stopEditing,
    } = props;

    const parsed = parseMonthsDetail(initialMonthsDetail);

    const [draftType, setDraftType] = useState<string>(initialType ?? "");
    const [draftBefore, setDraftBefore] = useState<string>(parsed.before);
    const [draftTreatment, setDraftTreatment] = useState<string>(
      parsed.treatment,
    );
    const [draftAfter, setDraftAfter] = useState<string>(parsed.after);
    const customText = useCustomTextEditor(
      initialCustomText,
      onCustomTextChange,
      stopEditing,
    );
    const { editingCustomText } = customText;

    const lastValueRef = useRef<unknown>(initialType ?? null);
    const beforeInputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
      beforeInputRef.current?.focus();
      beforeInputRef.current?.select();
    }, []);

    useImperativeHandle(ref, () => ({
      getValue: () => lastValueRef.current,
      isPopup: () => true,
      isCancelBeforeStart: () => false,
      isCancelAfterEnd: () => false,
    }));

    const showMonthInputs = draftType === "months";
    const beforeError = showMonthInputs
      ? validateMonth(draftBefore, monthsMax)
      : "";
    const treatmentError = showMonthInputs
      ? validateMonth(draftTreatment, monthsMax)
      : "";
    const afterError = showMonthInputs
      ? validateMonth(draftAfter, monthsMax)
      : "";
    const hasMonthError = !!beforeError || !!treatmentError || !!afterError;

    const handleTypeChange = (value: string): void => {
      setDraftType(value);
      if (value !== "months") {
        setDraftBefore("");
        setDraftTreatment("");
        setDraftAfter("");
      }
    };

    const handleSave = (): void => {
      if (editingCustomText) {
        lastValueRef.current = initialType ?? null;
        customText.saveCustomText(initialType);
        return;
      }

      if (hasMonthError) return;

      let detailString = "";
      if (draftType === "months") {
        const before = padMonth(draftBefore);
        const treatment = padMonth(draftTreatment);
        const after = draftAfter === "" ? "" : padMonth(draftAfter);

        if (after === "") {
          detailString = `${before}/${treatment}`;
        } else if (treatment === "00") {
          // A blank or zero treatment-free period reads the same as one the
          // carrier never stated, and the cell renders it as "before/after"
          // either way — store that string rather than a "00" middle segment
          // the display only strips again (CD-1905).
          detailString = `${before}/${after}`;
        } else {
          detailString = `${before}/${treatment}/${after}`;
        }
      }

      lastValueRef.current = draftType || null;

      const columnField = colDef?.field;
      if (initialCustomText) onCustomTextChange?.(null);

      if (columnField && node?.data) {
        writeCollapsedSiblings(
          node as { data: Record<string, unknown> },
          columnField,
          [
            [typeKey, draftType || null],
            [detailKey, detailString],
          ],
        );
      }

      stopEditing(true);

      setTimeout(() => {
        writeSibling({ api, column, colDef }, detailKey, detailString, {
          silent: true,
        });
        writeSibling({ api, column, colDef }, typeKey, draftType || null);
        if (node) api.refreshCells({ rowNodes: [node], force: true });
      }, 0);
    };

    const handleCancel = (): void => {
      lastValueRef.current = initialType ?? null;
      stopEditing(true);
    };

    const containerRef = useRef<HTMLDivElement | null>(null);
    usePopupEditorKeyboard(containerRef, handleSave, handleCancel);

    return (
      <Box
        ref={containerRef}
        sx={{
          p: 2,
          width: 440,
          backgroundColor: "background.paper",
          boxShadow: 3,
          borderRadius: 1,
        }}
      >
        <Stack spacing={1.5}>
          <Typography variant="subtitle1">
            <strong>Edit Benefit Value</strong>
          </Typography>

          {customText.customTextSupported && (
            <CustomTextModeToggle
              editMode={customText.editMode}
              onChange={customText.setEditMode}
            />
          )}

          {!editingCustomText && (
            <TextField
              select
              size="small"
              label="Type"
              value={draftType}
              onChange={(e) => handleTypeChange(e.target.value)}
              fullWidth
            >
              {typeOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          )}

          {!editingCustomText && showMonthInputs && (
            <>
              <TextField
                size="small"
                type="number"
                label="Diagnosis Free Before Eff Date (months)"
                value={draftBefore}
                onChange={(e) => setDraftBefore(e.target.value)}
                inputProps={{ min: 0, max: monthsMax }}
                error={!!beforeError}
                helperText={beforeError}
                fullWidth
                inputRef={beforeInputRef}
              />
              <TextField
                size="small"
                type="number"
                label="Treatment Free (months)"
                value={draftTreatment}
                onChange={(e) => setDraftTreatment(e.target.value)}
                inputProps={{ min: 0, max: monthsMax }}
                error={!!treatmentError}
                helperText={treatmentError}
                fullWidth
              />
              <TextField
                size="small"
                type="number"
                label="Diagnosis Free After Eff Date (months, optional)"
                value={draftAfter}
                onChange={(e) => setDraftAfter(e.target.value)}
                inputProps={{ min: 0, max: monthsMax }}
                error={!!afterError}
                helperText={afterError}
                fullWidth
              />
            </>
          )}

          {editingCustomText && (
            <CustomTextEditorFields
              content={customText.draftCustomText}
              onUpdate={customText.setDraftCustomText}
              onCharacterCount={customText.setDraftCharacterCount}
            />
          )}

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button size="sm" variant="secondary" onClick={handleCancel}>
              Cancel
            </Button>
            <Button size="sm" variant="primary" onClick={handleSave}>
              Save
            </Button>
          </Stack>
        </Stack>
      </Box>
    );
  },
);
